import React, { useState } from 'react';
import { Card, Tab, Tabs } from 'react-bootstrap';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { TrendPoint } from './DataDefinitions';

interface WorkLifeBalanceTrendProps {
    weeklyData: TrendPoint[];
    monthlyData: TrendPoint[];
    yearlyData: TrendPoint[];
}


const WorkLifeBalanceTrend: React.FC<WorkLifeBalanceTrendProps> = ({ weeklyData, monthlyData, yearlyData }) => {


    // Sample data - replace with your actual data


    console.log(weeklyData);
    console.log(monthlyData);

    /*
        const weeklyData: TrendPoint[] = [
            {
                week: 'Week 1',
                duration_personal: 25,
                duration_work: 45,
                balance_score: 70,
                date: '2024-01-01'
            },
            {
                week: 'Week 2',
                duration_personal: 30,
                duration_work: 40,
                balance_score: 80,
                date: '2024-01-08'
            },
            {
                week: 'Week 3',
                duration_personal: 20,
                duration_work: 50,
                balance_score: 60,
                date: '2024-01-15'
            }
        ];
        */

    /*
const monthlyData: TrendPoint[] = [
    {
        task_month: 'Jan',
        duration_personal: 100,
        duration_work: 180,
        balance_score: 75,
    },
    {
        task_month: 'Feb',
        duration_personal: 120,
        duration_work: 160,
        balance_score: 85,
    }
];*/
    /*
        const yearlyData: TrendPoint[] = [
            {
                year: '2021',
                duration_personal: 1200,
                duration_work: 2100,
                balance_score: 72,
                total_hours: 3300
            },
            {
                year: '2022',
                duration_personal: 1400,
                duration_work: 1900,
                balance_score: 85,
                total_hours: 3300
            },
            {
                year: '2023',
                duration_personal: 1500,
                duration_work: 1800,
                balance_score: 90,
                total_hours: 3300
            },
            {
                year: '2024',
                duration_personal: 400,
                duration_work: 500,
                balance_score: 88,
                total_hours: 900
            }
        ];
        */

    const getBalanceStatus = (score: number) => {
        if (score >= 80) return 'Excellent';
        if (score >= 60) return 'Good';
        if (score >= 40) return 'Fair';
        return 'Needs Improvement';
    };

    const calculateAverages = (data: TrendPoint[]) => {
        const totals = data.reduce((acc, curr) => ({
            duration_personal: acc.duration_personal + curr.duration_personal,
            duration_work: acc.duration_work + curr.duration_work,
            balance_score: (acc.balance_score ?? 0) + (curr.balance_score ?? 0) //?? 0 ensures that if balance_score is undefined or null, it defaults to 0.
        }), { duration_personal: 0, duration_work: 0, balance_score: 0 });

        return {
            duration_personal: (totals.duration_personal / data.length).toFixed(1),
            duration_work: (totals.duration_work / data.length).toFixed(1),
            balance_score: ((totals.balance_score ?? 0) / data.length).toFixed(1)
        };
    };

    const calculateYearOverYearChange = (): { personal: string; work: string; balance: string } => {
        if (yearlyData.length < 2) return { personal: "0", work: "0", balance: "0" };
        const currentYear = yearlyData[yearlyData.length - 1];
        const previousYear = yearlyData[yearlyData.length - 2];

        return {
            personal: ((currentYear.duration_personal - previousYear.duration_personal) / previousYear.duration_personal * 100).toFixed(1),
            work: ((currentYear.duration_work - previousYear.duration_work) / previousYear.duration_work * 100).toFixed(1),
            balance: (((currentYear.balance_score ?? 0) - (previousYear.balance_score ?? 0)) / (previousYear.balance_score ?? 1) * 100).toFixed(1)
        };
    };

    const weeklyAverages = calculateAverages(weeklyData);
    const monthlyAverages = calculateAverages(monthlyData);
    const yearlyAverages = calculateAverages(yearlyData);
    const yoyChange = calculateYearOverYearChange();

    const WeeklyTabView = () => {
        return (
            <div className="d-flex flex-column  gap-4">
                <div className="d-flex gap-4">
                    <Card className="flex-grow-1">
                        <Card.Header className='pb-0 border-bottom-0'>
                            <Card.Title>Current Balance Score</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <div className="text-4xl fw-bold mb-2">
                                {weeklyData.length > 0 ? (weeklyData[weeklyData.length - 1].balance_score ?? 0) : 0}/100
                            </div>
                            <div className="text-sm text-gray-500">
                                Status: {getBalanceStatus(weeklyData.length > 0 ? (weeklyData[weeklyData.length - 1].balance_score ?? 0) : 0)}
                            </div>
                        </Card.Body>
                    </Card>

                    <Card className="flex-grow-1">
                        <Card.Header className='pb-0 border-bottom-0'>
                            <Card.Title>Weekly Averages</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <div >
                                <div>Personal: {weeklyAverages.duration_personal} h</div>
                                <div>Professional: {weeklyAverages.duration_work} h</div>
                                <div>Balance Score: {weeklyAverages.balance_score}</div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>

                <Card className="md:col-span-2">
                    <Card.Header className='pb-0 border-bottom-0'>
                        <Card.Title>Weekly Trends</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <div className="vh-50">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={weeklyData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="week_number" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Line type="monotone" dataKey="duration_personal" stroke="#8884d8" name="Personal time" />
                                    <Line type="monotone" dataKey="duration_work" stroke="#82ca9d" name="Work time" />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    const MonthlyTabView = () => {
        return (
            <div className="d-flex flex-column  gap-4">
                <Card>
                    <Card.Header className='pb-0 border-bottom-0'>
                        <Card.Title>Monthly Averages</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <div >
                            <div>Personal: {monthlyAverages.duration_personal} h</div>
                            <div>Professional: {monthlyAverages.duration_work} h</div>
                            <div>Balance Score: {monthlyAverages.balance_score}</div>
                        </div>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Header className='pb-0 border-bottom-0'>
                        <Card.Title>Monthly Distribution</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <div className="vh-50">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={monthlyData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="task_month" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="duration_personal" fill="#8884d8" name="Personal time" />
                                    <Bar dataKey="duration_work" fill="#82ca9d" name="Work time" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    const YearlyTabView = () => {
        return (
            <div className="d-flex flex-column  gap-4">

                <div className="d-flex gap-4">
                    <Card className="flex-grow-1">
                        <Card.Header className='pb-0 border-bottom-0'>
                            <Card.Title>Year-over-Year Changes</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <div >
                                <div className="flex items-center">
                                    <span className="mr-2">Personal:</span>
                                    <span className={parseInt(yoyChange.personal) >= 0 ? "text-success" : "text-danger"}>
                                        {yoyChange.personal}%
                                    </span>
                                </div>
                                <div className="flex items-center">
                                    <span className="mr-2">Professional:</span>
                                    <span className={parseInt(yoyChange.work) >= 0 ? "text-success" : "text-danger"}>
                                        {yoyChange.work}%
                                    </span>
                                </div>
                                <div className="flex items-center">
                                    <span className="mr-2">Balance Score:</span>
                                    <span className={parseInt(yoyChange.balance) >= 0 ? "text-success" : "text-danger"}>
                                        {yoyChange.balance}%
                                    </span>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    <Card className="flex-grow-1">
                        <Card.Header className='pb-0 border-bottom-0'>
                            <Card.Title>Yearly Averages</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <div >
                                <div>Personal: {yearlyAverages.duration_personal} h</div>
                                <div>Professional: {yearlyAverages.duration_work} h</div>
                                <div>Balance Score: {yearlyAverages.balance_score}</div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>

                <Card className="md:col-span-2">
                    <Card.Header className='pb-0 border-bottom-0'>
                        <Card.Title>Yearly Trends</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <div className="vh-50">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={yearlyData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="task_year" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Area type="monotone" dataKey="duration_personal" stackId="1" stroke="#8884d8" fill="#8884d8" name="Personal time" />
                                    <Area type="monotone" dataKey="duration_work" stackId="1" stroke="#82ca9d" fill="#82ca9d" name="Work time" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </Card.Body>
                </Card>

                <Card className="md:col-span-2">
                    <Card.Header>
                        <Card.Title>Balance Score Trend</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <div className="vh-50">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={yearlyData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="year" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Line type="monotone" dataKey="balance_score" stroke="#ff7300" name="Balance Score" />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    };



    return (

        <Card style={{ 'overflow': 'auto', 'height': '100%' }}>
            <Card.Header className='pb-0 border-bottom-0'>
                <Card.Title>Work-Life Balance Dashboard</Card.Title>
            </Card.Header>
            <Card.Body className="pt-0">
                <Tabs defaultActiveKey="yearly" className="nav-underline mb-2">
                    {/* Weekly View Content */}
                    <Tab eventKey="weekly" title="weekly">
                        <WeeklyTabView />
                    </Tab>

                    {/* Monthly View Content */}
                    <Tab eventKey="monthly" title="monthly">
                        <MonthlyTabView />
                    </Tab>

                    {/* Yearly View Content */}
                    <Tab eventKey="yearly" title="yearly">
                        <YearlyTabView />
                    </Tab>
                </Tabs>
            </Card.Body>
        </Card>

    );
};

export default WorkLifeBalanceTrend;